import { deleteEntity, saveEntityPositions } from "api/entities";
import type { EntityDto } from "api/types";
import { CollaboratorsDialog, CollaboratorStack, RelationGroupChips, Spinner } from "components";
import { fieldLimits } from "config";
import { useActivationMilestone } from "hooks/useActivationMilestone";
import { useBrand } from "hooks/useBrand";
import { useCommandShortcut } from "hooks/useCommandShortcut";
import { useRelationGroup } from "hooks/useRelationGroup";
import React, { type ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSession } from "Session";
import { isApplePlatform } from "utilities";

import TreeGraph from "../../TreeViewPage/TreeGraph";
import type { NewEntitySeat, TreeGraphHandle, TreeLayoutMode } from "../../TreeViewPage/treeGraph.types";
import { entityGroups } from "../data";

import AddEntityDialog, { type EntityOption } from "./AddEntityDialog";
import CreatureFormDialog from "./CreatureFormDialog";
import EntityDetailsPanel from "./EntityDetailsPanel";
import { entityFormConfigsByLabel, entityFormConfigsByType } from "./entityFormConfigs";
import EntityFormDialog from "./EntityFormDialog";
import type { EntityFormConfig } from "./entityForms.types";
import LineIcon from "./LineIcon";
import MilestoneCelebration from "./MilestoneCelebration";
import ObjectFormDialog from "./ObjectFormDialog";
import PersonFormDialog from "./PersonFormDialog";
import PlaceFormDialog from "./PlaceFormDialog";
import SidebarEntityGroup from "./SidebarEntityGroup";
import type { EntityGroup, TreeEditorProps } from "./treeEditor.types";

import styles from "./TreeEditor.module.css";

// Mirrors the phone breakpoint in TreeEditor.module.css, where the entity rail turns into a top bar
// whose panel drops over the canvas. Keep both in sync.
const isPhoneLayout = (): boolean => window.matchMedia("(max-width: 780px)").matches;

// The entity added to this tree most recently. It is where a new node is seated when nothing has
// been worked on yet in this session - a tree opened and added to straight away - and it stands in
// for "where the user left off" well enough that the new node lands among the existing ones.
const lastCreatedEntityId = (entities: EntityDto[]): string | null =>
	entities.reduce<EntityDto | null>(
		(latest: EntityDto | null, entity: EntityDto) =>
			latest === null || (entity.createdDate ?? "") > (latest.createdDate ?? "") ? entity : latest,
		null
	)?.id ?? null;

// Fields the in-tree search scans. Covers the node name plus the common name-like traits
// (alias/nickname/title) so searching "Voldemort" or "Dark Lord" finds the node.
const matchesEntitySearch = (entity: EntityDto, term: string): boolean => {
	const haystacks: Array<string | null | undefined> = [
		entity.title,
		entity.alias,
		entity.nickname,
		entity.personTitle,
		entity.specialty,
		entity.speciesName,
		entity.creatureType,
		entity.customTypeLabel,
		entity.tags,
		entity.description
	];

	return haystacks.some((value: string | null | undefined) => value?.toLowerCase().includes(term));
};

const TreeEditor = ({
	collaborators = [],
	entities,
	initialSelectedEntityId = null,
	isLoading = false,
	onBack,
	onEditTree,
	onEntityCreated,
	onOpenTreeView,
	onSelectedEntityChange,
	onViewTree,
	readOnly = false,
	relations,
	treeId,
	treeTitle,
	treeVisibility
}: TreeEditorProps) => {
	const { t } = useTranslation("homePage");
	const { brand } = useBrand();
	const { getAccessToken, hasSession } = useSession();
	const [isAddEntityDialogOpen, setIsAddEntityDialogOpen] = useState(false);
	const [isPersonFormOpen, setIsPersonFormOpen] = useState(false);
	const [isPlaceFormOpen, setIsPlaceFormOpen] = useState(false);
	const [isObjectFormOpen, setIsObjectFormOpen] = useState(false);
	const [isCreatureFormOpen, setIsCreatureFormOpen] = useState(false);
	/** Config for the generic dialog (Organization/Event/Species/Concept/Custom); null when closed. */
	const [genericFormConfig, setGenericFormConfig] = useState<EntityFormConfig | null>(null);
	const [selectedEntityLabel, setSelectedEntityLabel] = useState("person");
	const [selectedEntityId, setSelectedEntityId] = useState<string | null>(initialSelectedEntityId);
	/**
	 * The entity most recently created, edited or selected. A new node is seated beside it, so
	 * whatever the user adds next turns up next to the work in progress instead of at the end of its
	 * generation row - which, on a wide tree, is off the right-hand edge of the canvas.
	 *
	 * A ref rather than state: nothing renders from it, and it has to survive the selection being
	 * cleared when the Add dialog opens (that is exactly the entity a new node should sit beside).
	 */
	const lastTouchedEntityIdRef = useRef<string | null>(initialSelectedEntityId);
	/** Nodes created in this session, in creation order, each paired with the anchor it sits beside. */
	const [newEntitySeats, setNewEntitySeats] = useState<NewEntitySeat[]>([]);
	const [editingEntity, setEditingEntity] = useState<EntityDto | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);
	const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({
		Concept: true,
		Creature: true,
		Custom: true,
		Event: true,
		Object: true,
		Organization: true,
		Person: true,
		Place: true,
		Species: true
	});
	// Phones open on the canvas with the entity panel behind its toggle; wider screens keep the rail out.
	const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(isPhoneLayout);
	const [isCollaboratorsOpen, setIsCollaboratorsOpen] = useState(false);
	/**
	 * Every toolbar action starts by clearing the selection, which closes the details panel and the
	 * ring of related entities around the selected node: a ring left open behind a dialog, a new
	 * layout or a save reads as stale (TTWEB-34).
	 */
	const clearSelection = useCallback((): void => {
		setSelectedEntityId(null);
		onSelectedEntityChange?.(null);
	}, [onSelectedEntityChange]);
	const openCollaborators = useCallback((): void => {
		clearSelection();
		setIsCollaboratorsOpen(true);
	}, [clearSelection]);
	const closeCollaborators = useCallback((): void => setIsCollaboratorsOpen(false), []);

	// The activation moment: five connected nodes. A read-only viewer is excluded — they did not
	// build this tree, so there is nothing to congratulate them for.
	const { dismiss: dismissMilestone, isCelebrating } = useActivationMilestone(treeId, entities.length, !readOnly);
	// Publishing, sharing and inviting all live on the tree view, so the handoff goes there.
	const onCelebrationShare = useCallback((): void => {
		dismissMilestone();
		onOpenTreeView();
	}, [dismissMilestone, onOpenTreeView]);
	// Which relation context the canvas draws, remembered per tree for this viewer.
	const { relationGroup, setRelationGroup } = useRelationGroup(treeId);
	const [layoutMode, setLayoutMode] = useState<TreeLayoutMode>("hierarchical");
	const onSelectHierarchicalLayout = useCallback((): void => {
		clearSelection();
		setLayoutMode("hierarchical");
	}, [clearSelection]);
	const onSelectRadialLayout = useCallback((): void => {
		clearSelection();
		setLayoutMode("radial");
	}, [clearSelection]);
	const onSelectForceLayout = useCallback((): void => {
		clearSelection();
		setLayoutMode("force");
	}, [clearSelection]);
	const hasEntities = entities.length > 0;
	const selectedEntity = useMemo(
		() => entities.find((entity: EntityDto) => entity.id === selectedEntityId) ?? null,
		[entities, selectedEntityId]
	);
	const entitiesByType = useMemo(() => {
		const groups = new Map<string, EntityDto[]>();
		entities.forEach((entity: EntityDto) => {
			if (!groups.has(entity.type)) {
				groups.set(entity.type, []);
			}
			groups.get(entity.type)!.push(entity);
		});

		return groups;
	}, [entities]);

	// In-tree search: filters the sidebar to this tree's matching nodes only.
	const [entitySearch, setEntitySearch] = useState("");
	const searchTerm = entitySearch.trim().toLowerCase();
	const isSearching = searchTerm.length > 0;
	const onEntitySearchChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>): void => setEntitySearch(event.target.value),
		[]
	);
	const clearEntitySearch = useCallback((): void => setEntitySearch(""), []);

	const filteredEntitiesByType = useMemo(() => {
		if (!isSearching) {
			return entitiesByType;
		}

		const groups = new Map<string, EntityDto[]>();
		entitiesByType.forEach((items: EntityDto[], type: string) => {
			const matched = items.filter((entity: EntityDto) => matchesEntitySearch(entity, searchTerm));
			if (matched.length > 0) {
				groups.set(type, matched);
			}
		});

		return groups;
	}, [entitiesByType, isSearching, searchTerm]);

	const visibleGroups = useMemo<EntityGroup[]>(
		() =>
			isSearching
				? entityGroups.filter((group: EntityGroup) => filteredEntitiesByType.has(group.type))
				: entityGroups,
		[filteredEntitiesByType, isSearching]
	);

	const toggleGroup = useCallback((type: string): void => {
		setExpandedGroups((current: Record<string, boolean>) => ({ ...current, [type]: !current[type] }));
	}, []);
	const toggleSidebar = useCallback((): void => setIsSidebarCollapsed((collapsed: boolean) => !collapsed), []);
	// Clicking a compact group re-opens the sidebar with that group expanded.
	const onCompactGroupSelect = useCallback((type: string): void => {
		setIsSidebarCollapsed(false);
		setExpandedGroups((current: Record<string, boolean>) => ({ ...current, [type]: true }));
	}, []);
	const entityOptions = useMemo<EntityOption[]>(
		() => [
			{
				description: t("editor.addEntityDialog.personDescription"),
				icon: "person",
				label: t("editor.addEntityDialog.person"),
				value: "person"
			},
			{
				description: t("editor.addEntityDialog.placeDescription"),
				icon: "place",
				label: t("editor.addEntityDialog.place"),
				value: "place"
			},
			{
				description: t("editor.addEntityDialog.objectDescription"),
				icon: "object",
				label: t("editor.addEntityDialog.object"),
				value: "object"
			},
			{
				description: t("editor.addEntityDialog.creatureDescription"),
				icon: "creature",
				label: t("editor.addEntityDialog.creature"),
				value: "creature"
			},
			{
				description: t("editor.addEntityDialog.organizationDescription"),
				icon: "organization",
				label: t("editor.addEntityDialog.organization"),
				value: "organization"
			},
			{
				description: t("editor.addEntityDialog.eventDescription"),
				icon: "event",
				label: t("editor.addEntityDialog.event"),
				value: "event"
			},
			{
				description: t("editor.addEntityDialog.speciesDescription"),
				icon: "species",
				label: t("editor.addEntityDialog.species"),
				value: "species"
			},
			{
				description: t("editor.addEntityDialog.conceptDescription"),
				icon: "sparkle",
				label: t("editor.addEntityDialog.concept"),
				value: "concept"
			},
			{
				description: t("editor.addEntityDialog.customDescription"),
				icon: "custom",
				label: t("editor.addEntityDialog.custom"),
				value: "custom"
			}
		],
		[t]
	);
	const openAddEntityDialog = useCallback((): void => {
		// Close the entity details panel so only one panel is open at a time.
		setSelectedEntityId(null);
		onSelectedEntityChange?.(null);
		setIsAddEntityDialogOpen(true);
	}, [onSelectedEntityChange]);
	const closeAddEntityDialog = useCallback((): void => {
		setIsAddEntityDialogOpen(false);
	}, []);
	const continueAddEntityDialog = useCallback((): void => {
		setIsAddEntityDialogOpen(false);
		if (selectedEntityLabel === "person") {
			setIsPersonFormOpen(true);
		} else if (selectedEntityLabel === "place") {
			setIsPlaceFormOpen(true);
		} else if (selectedEntityLabel === "object") {
			setIsObjectFormOpen(true);
		} else if (selectedEntityLabel === "creature") {
			setIsCreatureFormOpen(true);
		} else if (entityFormConfigsByLabel[selectedEntityLabel]) {
			setGenericFormConfig(entityFormConfigsByLabel[selectedEntityLabel]);
		}
	}, [selectedEntityLabel]);
	const closePersonFormDialog = useCallback((): void => {
		setIsPersonFormOpen(false);
		setEditingEntity(null);
	}, []);
	const closePlaceFormDialog = useCallback((): void => {
		setIsPlaceFormOpen(false);
		setEditingEntity(null);
	}, []);
	const closeObjectFormDialog = useCallback((): void => {
		setIsObjectFormOpen(false);
		setEditingEntity(null);
	}, []);
	const closeCreatureFormDialog = useCallback((): void => {
		setIsCreatureFormOpen(false);
		setEditingEntity(null);
	}, []);
	const closeGenericFormDialog = useCallback((): void => {
		setGenericFormConfig(null);
		setEditingEntity(null);
	}, []);
	const onSelectEntity = useCallback(
		(entity: EntityDto): void => {
			setSelectedEntityId(entity.id);
			lastTouchedEntityIdRef.current = entity.id;
			onSelectedEntityChange?.(entity.id);
			// On phones the panel covers the canvas, so picking something has to close it again.
			if (isPhoneLayout()) {
				setIsSidebarCollapsed(true);
			}
		},
		[onSelectedEntityChange]
	);
	const closeEntityDetails = useCallback((): void => {
		setSelectedEntityId(null);
		onSelectedEntityChange?.(null);
	}, [onSelectedEntityChange]);
	// Every entity form reports the id it saved. `entities` here is still the list from before the
	// refetch, so an id missing from it is a node that did not exist a moment ago: record the seat it
	// should take, beside whatever was last worked on. Anything else is an edit, which only moves the
	// anchor along. A stale anchor (its entity deleted since) falls back to the newest entity, so the
	// seat always points at something the canvas can actually place a node next to.
	const onEntitySaved = useCallback(
		(entityId: string): void => {
			const touched = lastTouchedEntityIdRef.current;
			const isKnown = (id: string | null): boolean =>
				id !== null && entities.some((entity: EntityDto) => entity.id === id);
			if (!isKnown(entityId)) {
				const anchorId = isKnown(touched) ? touched : lastCreatedEntityId(entities);
				if (anchorId !== null) {
					setNewEntitySeats((current: NewEntitySeat[]) => [...current, { entityId, anchorId }]);
				}
			}

			lastTouchedEntityIdRef.current = entityId;
			onEntityCreated();
		},
		[entities, onEntityCreated]
	);
	// Explicit layout save: snapshots where every entity currently sits and persists it, so the
	// arrangement is restored on load and by Reset Layout. Edit mode only — nodes cannot be moved on
	// a read-only canvas, so there is never an arrangement to save there, not even for an Admin.
	const graphRef = useRef<TreeGraphHandle>(null);
	const [layoutSaveState, setLayoutSaveState] = useState<"idle" | "saving" | "saved">("idle");
	const showSaveLayout = hasEntities && layoutMode === "hierarchical" && !readOnly;

	/**
	 * Writes the seats the canvas is showing right now. Shared by the Save Layout button and by
	 * leaving edit mode, which saves on the way out rather than dropping the arrangement at the
	 * door. Throws whatever the API threw, so a caller that must not navigate on a failed save can
	 * stay put; returns false only when there is no session left to save with.
	 */
	const persistLayout = useCallback(async (): Promise<boolean> => {
		const snapshot = graphRef.current?.getCanvasPositions();
		if (!snapshot || snapshot.entities.length === 0) {
			return true;
		}

		const token = await getAccessToken();
		if (!token) {
			return false;
		}

		await saveEntityPositions(treeId, snapshot.entities, snapshot.links, token);

		return true;
	}, [getAccessToken, treeId]);

	const onSaveLayout = useCallback(async (): Promise<void> => {
		if (layoutSaveState === "saving") {
			return;
		}

		setLayoutSaveState("saving");
		try {
			if (await persistLayout()) {
				setLayoutSaveState("saved");
				// Refetch so the entities carry the just-saved coordinates.
				onEntityCreated();
			} else {
				setLayoutSaveState("idle");
			}
		} catch (error: unknown) {
			setLayoutSaveState("idle");
			// eslint-disable-next-line no-alert
			window.alert(error instanceof Error ? error.message : t("editor.saveLayoutFailed"));
		}
	}, [layoutSaveState, onEntityCreated, persistLayout, t]);
	// The button closes the ring like every other toolbar action; Ctrl+S saves and leaves it be.
	const onSaveLayoutClick = useCallback((): void => {
		clearSelection();
		onSaveLayout().catch(() => undefined);
	}, [clearSelection, onSaveLayout]);

	/**
	 * Leaving edit mode for the read-only view. Entities and relations are already on the server by
	 * the time their dialog closes, but seats are not — they are written only by an explicit action,
	 * and this is one. So the arrangement travels with the user instead of being lost, including the
	 * seats of entities added this session, which have no saved position at all until now.
	 */
	const onLeaveForView = useCallback(async (): Promise<void> => {
		if (!onViewTree || layoutSaveState === "saving") {
			return;
		}

		clearSelection();

		// The same gate the Save Layout button uses: saved positions are replayed into the
		// hierarchical layout, so radial and force coordinates are not this tree's arrangement and
		// must never be written over it. Those modes recompute from scratch anyway, so there is
		// nothing to lose by leaving them unsaved.
		if (!showSaveLayout) {
			onViewTree();

			return;
		}

		setLayoutSaveState("saving");
		try {
			await persistLayout();
		} catch (error: unknown) {
			setLayoutSaveState("idle");
			// eslint-disable-next-line no-alert
			window.alert(error instanceof Error ? error.message : t("editor.saveLayoutFailed"));

			// Stay in edit mode: leaving now is exactly the silent loss this guards against.
			return;
		}

		setLayoutSaveState("idle");
		onViewTree();
	}, [clearSelection, layoutSaveState, onViewTree, persistLayout, showSaveLayout, t]);

	// Ctrl+S (Cmd+S on macOS) saves the layout instead of opening the browser's "Save page" dialog.
	// Bound only while the button itself is on screen, so it never fires where saving is not offered.
	useCommandShortcut("s", onSaveLayout, showSaveLayout);

	// Ctrl+Z walks back the drags made since the canvas was built — node positions only. Nothing
	// recorded (entities, relations, links) is undoable this way; those have their own edit and
	// delete actions. Bound in edit mode in every layout mode, and nowhere else: a read-only canvas
	// has no drags to walk back.
	const onUndoMove = useCallback((): void => graphRef.current?.undoLastMove(), []);
	useCommandShortcut("z", onUndoMove, hasEntities && !readOnly);

	const saveLayoutHint = t("editor.saveLayoutHint", {
		action: t("editor.saveLayout"),
		shortcut: t(isApplePlatform() ? "editor.saveLayoutShortcutMac" : "editor.saveLayoutShortcut")
	});

	// Show the "Saved" confirmation briefly, then fall back to the idle label.
	useEffect(() => {
		if (layoutSaveState !== "saved") {
			return undefined;
		}

		const timer = window.setTimeout((): void => setLayoutSaveState("idle"), 2000);

		return () => window.clearTimeout(timer);
	}, [layoutSaveState]);

	// Reset Layout: remounting the canvas rebuilds it from the saved positions (or the auto-layout
	// when none are saved), discarding any unsaved drags, and re-fits the viewport. Local only —
	// it never touches the positions saved on the server.
	const [canvasResetKey, setCanvasResetKey] = useState(0);
	const onResetLayout = useCallback((): void => {
		clearSelection();
		setCanvasResetKey((key: number) => key + 1);
	}, [clearSelection]);

	// Follow external selection changes (e.g. browser navigation updating the URL).
	useEffect(() => {
		setSelectedEntityId(initialSelectedEntityId);
		if (initialSelectedEntityId !== null) {
			lastTouchedEntityIdRef.current = initialSelectedEntityId;
		}
	}, [initialSelectedEntityId]);
	const onEditSelected = useCallback((): void => {
		if (!selectedEntity) {
			return;
		}

		setEditingEntity(selectedEntity);
		if (selectedEntity.type === "Place") {
			setIsPlaceFormOpen(true);
		} else if (selectedEntity.type === "Object") {
			setIsObjectFormOpen(true);
		} else if (selectedEntity.type === "Creature") {
			setIsCreatureFormOpen(true);
		} else if (entityFormConfigsByType[selectedEntity.type]) {
			setGenericFormConfig(entityFormConfigsByType[selectedEntity.type]);
		} else {
			setIsPersonFormOpen(true);
		}
	}, [selectedEntity]);
	const onDeleteSelected = useCallback(async (): Promise<void> => {
		if (!selectedEntity || isDeleting) {
			return;
		}

		// eslint-disable-next-line no-alert
		if (!window.confirm(t("editor.entityPanel.confirmDelete", { name: selectedEntity.title }))) {
			return;
		}

		const token = await getAccessToken();
		if (!token) {
			return;
		}

		setIsDeleting(true);
		try {
			await deleteEntity(selectedEntity.id, token);
			setSelectedEntityId(null);
			onEntityCreated();
		} catch {
			// Keep the panel open so the user can retry.
		} finally {
			setIsDeleting(false);
		}
	}, [getAccessToken, isDeleting, onEntityCreated, selectedEntity, t]);

	return (
		<div
			className={
				isSidebarCollapsed ? `${styles.editorShell} ${styles.editorShellCollapsed}` : styles.editorShell
			}>
			<aside aria-label={t("editor.entitiesLabel")} className={styles.editorSidebar}>
				<div className={isSidebarCollapsed ? styles.editorBrandCollapsed : styles.editorBrand}>
					<img
						alt={brand}
						className={styles.editorLogo}
						src={`${process.env.PUBLIC_URL}/assets/images/logo.png`}
					/>
					{/* Always rendered: the narrow desktop rail hides it in CSS, the phone bar keeps it. */}
					<span className={styles.editorWordmark}>{brand}</span>
				</div>

				<button
					aria-expanded={!isSidebarCollapsed}
					aria-label={isSidebarCollapsed ? t("editor.expandSidebar") : t("editor.collapseSidebar")}
					className={styles.sidebarToggle}
					onClick={toggleSidebar}
					title={isSidebarCollapsed ? t("editor.expandSidebar") : t("editor.collapseSidebar")}
					type="button">
					<LineIcon name={isSidebarCollapsed ? "chevronRight" : "chevronLeft"} />
					<span>{isSidebarCollapsed ? t("editor.expandSidebar") : t("editor.collapseSidebar")}</span>
				</button>

				<div className={isSidebarCollapsed ? styles.entityPanelCollapsed : styles.entityPanel}>
					{/* Plain text, not a second Spinner: the canvas already owns the live region, and two
					    of them would announce the same thing twice. */}
					{!hasEntities && !isSidebarCollapsed && (
						<div className={styles.emptyEntityState}>
							{isLoading ? t("view.loadingEntities") : t("editor.emptyEntities")}
						</div>
					)}

					{/* The tree's size at a glance. Counted off the loaded entities rather than the tree
					    row's `entityCount`, so it always agrees with the per-type counts below it. */}
					{hasEntities && !isSidebarCollapsed && (
						<div className={styles.entityTotal}>
							<LineIcon name="tree" />
							<span>{t("library.entityCount", { count: entities.length })}</span>
						</div>
					)}

					{hasEntities && !isSidebarCollapsed && (
						<label className={styles.entitySearchField} htmlFor="tree-entity-search">
							<LineIcon name="search" />
							<span className="sr-only">{t("editor.searchEntitiesLabel")}</span>
							<input
								autoComplete="off"
								id="tree-entity-search"
								maxLength={fieldLimits.search}
								onChange={onEntitySearchChange}
								placeholder={t("editor.searchEntitiesPlaceholder")}
								type="search"
								value={entitySearch}
							/>
							{isSearching && (
								<button
									aria-label={t("editor.clearSearch")}
									className={styles.entitySearchClear}
									onClick={clearEntitySearch}
									type="button">
									<LineIcon name="close" />
								</button>
							)}
						</label>
					)}

					<div className={styles.entityGroupList}>
						{visibleGroups.map((group: EntityGroup) => (
							<SidebarEntityGroup
								key={group.type}
								group={group}
								isCompact={isSidebarCollapsed}
								isExpanded={isSearching || Boolean(expandedGroups[group.type])}
								items={filteredEntitiesByType.get(group.type) ?? []}
								onCompactSelect={onCompactGroupSelect}
								onSelect={onSelectEntity}
								onToggle={toggleGroup}
								selectedEntityId={selectedEntityId}
							/>
						))}

						{isSearching && !isSidebarCollapsed && visibleGroups.length === 0 && (
							<div className={styles.entitySearchEmpty}>
								{t("editor.noSearchResults", { term: entitySearch.trim() })}
							</div>
						)}
					</div>
				</div>
			</aside>

			<main className={styles.editorMain}>
				<header className={styles.editorTopbar}>
					<div className={styles.treeTitleCluster}>
						<div className={styles.treeBreadcrumb}>
							{/* A signed-out reader of a public tree has no "My Trees" to go back to. */}
							<button onClick={onBack} type="button">
								{t(hasSession ? "editor.breadcrumbRoot" : "editor.breadcrumbHome")}
							</button>
							<span>/</span>
							<button
								className={styles.treeBreadcrumbCurrent}
								onClick={onOpenTreeView}
								title={t("editor.breadcrumbViewTree")}
								type="button">
								{treeTitle}
							</button>
						</div>
						<span className={styles.editorPrivacyBadge}>
							<LineIcon name="lock" />
							{treeVisibility}
						</span>
						{collaborators.length > 1 && (
							<CollaboratorStack
								collaborators={collaborators}
								label={t("view.collaboratorsWithCount", { count: collaborators.length })}
								onClick={openCollaborators}
								showLabel={false}
							/>
						)}
					</div>

					<div aria-label={t("editor.canvasControls")} className={styles.canvasControlCluster}>
						<div aria-label={t("editor.treeLayout")} className={styles.layoutGroup}>
							<button
								aria-pressed={layoutMode === "hierarchical"}
								className={layoutMode === "hierarchical" ? styles.layoutButtonActive : undefined}
								onClick={onSelectHierarchicalLayout}
								type="button">
								<LineIcon name="tree" />
								<span>{t("editor.hierarchical")}</span>
							</button>
							<button
								aria-pressed={layoutMode === "radial"}
								className={layoutMode === "radial" ? styles.layoutButtonActive : undefined}
								onClick={onSelectRadialLayout}
								type="button">
								<LineIcon name="compass" />
								<span>{t("editor.radial")}</span>
							</button>
							<button
								aria-pressed={layoutMode === "force"}
								className={layoutMode === "force" ? styles.layoutButtonActive : undefined}
								onClick={onSelectForceLayout}
								type="button">
								<LineIcon name="settings" />
								<span>{t("editor.force")}</span>
							</button>
						</div>

						{hasEntities && (
							<button className={styles.editorEditButton} onClick={onResetLayout} type="button">
								<LineIcon name="refresh" />
								<span>{t("editor.resetLayout")}</span>
							</button>
						)}

						{onEditTree && (
							<button className={styles.editorEditButton} onClick={onEditTree} type="button">
								<LineIcon name="pencil" />
								<span>{t("view.edit")}</span>
							</button>
						)}

						{onViewTree && (
							<button
								className={styles.editorEditButton}
								disabled={layoutSaveState === "saving"}
								onClick={onLeaveForView}
								title={t("editor.viewModeHint")}
								type="button">
								<LineIcon name="eye" />
								<span>{t("editor.viewMode")}</span>
							</button>
						)}
					</div>
				</header>

				{hasEntities && (
					<div className={styles.relationGroupBar}>
						<RelationGroupChips onChange={setRelationGroup} value={relationGroup} />
					</div>
				)}

				<div className={styles.editorContent}>
					<section aria-label={t("editor.canvasLabel")} className={styles.editorCanvas}>
						{!hasEntities && isLoading && (
							<div className={styles.loadingCanvasState}>
								<Spinner label={t("view.loadingEntities")} />
							</div>
						)}

						{!hasEntities && !isLoading && (
							<div className={styles.emptyCanvasState}>
								<div className={styles.startTreePanel}>
									<img
										alt=""
										className={styles.startTreeMark}
										src={`${process.env.PUBLIC_URL}/assets/images/logo.png`}
									/>
									<h1>{t("editor.startTitle")}</h1>
									<p>
										{t("editor.startDescription")
											.split("\n")
											.map((line: string, index: number) => (
												<span key={line}>
													{index > 0 && <br />}
													{line}
												</span>
											))}
									</p>
									{!readOnly && (
										<div className={styles.startTreeActions}>
											<button onClick={openAddEntityDialog} type="button">
												<LineIcon name="plus" />
												<span>{t("editor.addFirstEntity")}</span>
											</button>
										</div>
									)}
								</div>
							</div>
						)}

						{hasEntities && (
							<TreeGraph
								key={canvasResetKey}
								ref={graphRef}
								entities={entities}
								layoutMode={layoutMode}
								newEntitySeats={newEntitySeats}
								// Clicking empty canvas deselects, which also closes the selected node's ring.
								onBackgroundClick={closeEntityDetails}
								onSelectEntity={onSelectEntity}
								readOnly={readOnly}
								relationGroup={relationGroup}
								relations={relations}
								selectedEntityId={selectedEntityId}
							/>
						)}

						{showSaveLayout && (
							<button
								className={`${styles.editorEditButton} ${styles.saveLayoutButton}`}
								disabled={layoutSaveState === "saving"}
								onClick={onSaveLayoutClick}
								title={saveLayoutHint}
								type="button">
								<LineIcon name="save" />
								<span>
									{layoutSaveState === "saving" && t("editor.savingLayout")}
									{layoutSaveState === "saved" && t("editor.layoutSaved")}
									{layoutSaveState === "idle" && t("editor.saveLayout")}
								</span>
							</button>
						)}

						{hasEntities && !readOnly && (
							<button
								aria-label={t("editor.addEntityDialog.fabLabel")}
								className={styles.addEntityFab}
								onClick={openAddEntityDialog}
								type="button">
								<span className={styles.addEntityFabIcon}>
									<LineIcon name="plus" />
								</span>
								<span className={styles.addEntityFabLabel}>{t("editor.addEntityDialog.fabLabel")}</span>
							</button>
						)}
					</section>

					{selectedEntity && (
						<EntityDetailsPanel
							entities={entities}
							entity={selectedEntity}
							isDeleting={isDeleting}
							onClose={closeEntityDetails}
							onDelete={readOnly ? undefined : onDeleteSelected}
							onEdit={readOnly ? undefined : onEditSelected}
							onSelectEntity={onSelectEntity}
							relations={relations}
						/>
					)}
				</div>
			</main>

			{isCollaboratorsOpen && <CollaboratorsDialog collaborators={collaborators} onClose={closeCollaborators} />}

			{isAddEntityDialogOpen && !readOnly && (
				<AddEntityDialog
					onClose={closeAddEntityDialog}
					onContinue={continueAddEntityDialog}
					onSelect={setSelectedEntityLabel}
					options={entityOptions}
					selectedOption={selectedEntityLabel}
				/>
			)}
			{isPersonFormOpen && !readOnly && (
				<PersonFormDialog
					entity={editingEntity}
					onClose={closePersonFormDialog}
					onCreated={onEntitySaved}
					treeId={treeId}
				/>
			)}
			{isPlaceFormOpen && !readOnly && (
				<PlaceFormDialog
					entity={editingEntity}
					onClose={closePlaceFormDialog}
					onCreated={onEntitySaved}
					treeId={treeId}
				/>
			)}
			{isObjectFormOpen && !readOnly && (
				<ObjectFormDialog
					entity={editingEntity}
					onClose={closeObjectFormDialog}
					onCreated={onEntitySaved}
					treeId={treeId}
				/>
			)}
			{isCreatureFormOpen && !readOnly && (
				<CreatureFormDialog
					entity={editingEntity}
					onClose={closeCreatureFormDialog}
					onCreated={onEntitySaved}
					treeId={treeId}
				/>
			)}
			{genericFormConfig && !readOnly && (
				<EntityFormDialog
					config={genericFormConfig}
					entity={editingEntity}
					onClose={closeGenericFormDialog}
					onCreated={onEntitySaved}
					treeId={treeId}
				/>
			)}
			{isCelebrating && (
				<MilestoneCelebration
					nodeCount={entities.length}
					onKeepBuilding={dismissMilestone}
					onShare={onCelebrationShare}
					treeTitle={treeTitle}
				/>
			)}
		</div>
	);
};

export default TreeEditor;
